import {useCallback, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {Navigate, useNavigate, useSearchParams} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  addDays,
  extractApiErrorMessage,
  personType,
  startOfDay,
  subDays,
  timeZone,
  TransportAssocEntity,
  TransportEntity,
  useDriverActivityPrefetch,
  useGetDriverActivityByDayQuery,
  useGetTtaAuditSummariesQuery,
  useLazyGetAttachmentBundlesQuery,
  useUpdateTransportMutation
} from "@ots-frontend/ots-data";
import {ResizeableContent, useApp, useAuth, useTicketFormPageNavigate} from "@ots-frontend/ots-ui";
import {
  ActivityAttachment,
  ActivityEntry,
  ActivityFacetKey,
  ActivityFacets,
  activeFacetCount,
  ConnectedActivityRouteMap,
  ActivityTransportEdit,
  ConnectedTicketAuditPanel,
  ActivityViewFilter,
  DEFAULT_DRIVER_ACTIVITY_DISPLAY,
  DRIVER_ACTIVITY_ACTIONS,
  DriverActivityDisplayOptions,
  DriverActivityViewState,
  DriverActivityWorkspace,
  combineActionProviders,
  createActionProvider,
  DriverActivityAction,
  ConnectedActivityDayMap,
  useDriverActivityBulkEditTools,
  useDriverActivityCreateTools,
  useDriverActivityQuantityTools,
  useDriverActivityShiftCostTools,
  useDriverActivityStructureTools,
  useDriverActivityTicketEditTools,
  useDriverActivityTicketTools,
  useOtsToast
} from "@ots-frontend/ots-ui-tw";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {setAppDate} from "../../store/ui/actions";
import {toFormDefaultsFromTta} from "../DispatchFormPage/DispatchFormPage";
import {ticketFormNavigation} from "./driver-activity-form-state";

// Driver activity hides every cost view for now; flip this to show them to those who may see costs.
const COST_VIEWS_ENABLED = false;

// View, Map, and Edit open DispatchGo's ticket pages, as the ticket grid does, along with its dispatch actions.
// The ticket tools (comments, attachments, compliance, downloads), ticket edits (validation, demurrage,
// unmatching, deleting), and new shifts and tickets come from the shared hooks, with their dialogs.
const PAGE_ACTIONS: DriverActivityAction[] = [
  ...DRIVER_ACTIVITY_ACTIONS.filter(action => action.id === "view" || action.id === "map" || action.id === "edit")
    .map(action => ({
      ...action,
      scope: "single" as const,
      // A ticket opens its ticket form; an unmatched pickup or delivery, its transport form.
      isVisible: action.id === "edit" ? undefined : ([entry]: ActivityEntry[]) => !!entry?.tta,
    })),
  {
    id: "view-dispatch", label: "View Dispatch", scope: "single", group: "tools",
    isVisible: ([entry]) => !!entry?.tta?.dispatchId,
  },
  {id: "new-dispatch", label: "New Dispatch", scope: "single", group: "create", isVisible: ([entry]) => !!entry?.tta},
];

/** What each page action needs; the rest are open to anyone who can see the page. */
const PAGE_ACTION_PERMISSIONS: Record<string, string> = {
  edit: "transporttransportassoc_edit",
  "view-dispatch": "dispatch_view",
  "new-dispatch": "dispatch_create",
};

const DISPLAY_KEY = "dispatch-go.driver-activity.display";

/** WaterGo's restricted filters: people and units, not trailers or the companies on the tickets. */
const RESTRICTED_FACETS: ActivityFacetKey[] = ["person", "vehicle"];

/** Starts each download in its own hidden frame, so several can run without leaving the page. */
const startDownloads = (urls: string[]) => {
  for (const url of urls) {
    const frame = Object.assign(document.createElement("iframe"), {src: url, hidden: true});
    document.body.appendChild(frame);
    window.setTimeout(() => frame.remove(), 60_000);
  }
};

/** The filters a link carries, as the workspace reported them; a damaged one is ignored. */
const readFacets = (text: string | null): ActivityFacets | undefined => {
  if (!text) return undefined;
  try {
    const facets = JSON.parse(text);
    return facets && typeof facets === "object" ? facets as ActivityFacets : undefined;
  } catch {
    return undefined;
  }
};

const readDisplay = (): DriverActivityDisplayOptions => {
  try {
    return {...DEFAULT_DRIVER_ACTIVITY_DISPLAY, ...JSON.parse(localStorage.getItem(DISPLAY_KEY) ?? "{}")};
  } catch {
    return DEFAULT_DRIVER_ACTIVITY_DISPLAY;
  }
};

const saveDisplay = (display: DriverActivityDisplayOptions) => {
  try {
    localStorage.setItem(DISPLAY_KEY, JSON.stringify(display));
  } catch {
    // Private browsing or a full quota: the choice simply lasts for this visit.
  }
};

/** Tailwind driver activity workspace on the same day query as `DriverActivityDayPage`. */
export const DriverActivityWorkspacePage = () => {
  const appDttm = useAppSelector(getAppDttm);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {regionId, preferences, googleMapsApiKey} = useApp();
  const prefetch = useDriverActivityPrefetch("getDriverActivityByDay");
  // The search, quick view, and open ticket live in the URL, so a link reopens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialView] = useState<Partial<DriverActivityViewState>>(() => ({
    query: searchParams.get("q") ?? undefined,
    view: (searchParams.get("view") as ActivityViewFilter | null) ?? undefined,
    activeKey: searchParams.get("open") ?? undefined,
    facets: readFacets(searchParams.get("filters")),
  }));
  const [initialDisplay] = useState(readDisplay);
  // The picked rate models are also what a shift is costed with.
  const [display, setDisplay] = useState(initialDisplay);
  const onDisplayChange = useCallback((next: DriverActivityDisplayOptions) => {
    setDisplay(next);
    saveDisplay(next);
  }, []);

  const reportTime = preferences?.reportDefaultTime ?? 0;
  const startShiftReport = preferences?.reportDefaultReportTimeField !== "TRANSPORT";

  const argFor = useCallback((date: Date | number) => regionId
    ? {day: startOfDay(date).getTime() + reportTime, regionId, startShiftReport}
    : undefined, [regionId, reportTime, startShiftReport]);

  const queryArg = useMemo(() => argFor(appDttm) ?? skipToken, [argFor, appDttm]);
  const {data, isLoading, isFetching, error, refetch, fulfilledTimeStamp} = useGetDriverActivityByDayQuery(queryArg);

  // Audits are not part of the activity rows; they are fetched for the day's tickets in one request.
  const auditTargets = useMemo(() => (data ?? []).flatMap(row => row.transportTransportAssoc
    ? [{objectId: row.transportTransportAssoc.id, tenantCode: row.transportTransportAssoc.database}]
    : []), [data]);
  const {data: audits} = useGetTtaAuditSummariesQuery(auditTargets.length ? {targets: auditTargets} : skipToken);

  // Download links are signed and short-lived, so they are asked for at the moment of the click.
  const [fetchAttachmentBundles] = useLazyGetAttachmentBundlesQuery();
  const toast = useOtsToast();
  const onDownloadAttachments = useCallback(async (attachments: ActivityAttachment[]) => {
    const targets = [...new Map(attachments.map(({objectId, objectType, tenantCode}) => [objectId, {objectId, objectType, tenantCode}])).values()];
    try {
      const bundles = await fetchAttachmentBundles(targets).unwrap();
      const urls = attachments.length === 1
        ? [bundles.flatMap(({bundle}) => bundle.attachments).find(file => file.id === attachments[0].id)?.downloadUrl ?? attachments[0].url]
        : bundles.map(({bundle}) => bundle.bulkDownloadUrl);
      startDownloads(urls.filter((url): url is string => !!url));
    } catch (failure) {
      toast.show({
        title: "Download failed",
        description: extractApiErrorMessage(failure) ?? "The attachments could not be downloaded.",
        tone: "danger",
      });
    }
  }, [fetchAttachmentBundles, toast]);

  // Corrections made in the grid save the whole transport, as the ticket form does. The mutation merges the
  // saved transport into the cached day, so the day is not fetched again.
  const [updateTransport] = useUpdateTransportMutation();
  const onEditTransport = useCallback(async (entry: ActivityEntry, change: ActivityTransportEdit) => {
    const transport = change.side === "pickup" ? entry.pickup : entry.delivery;
    if (!transport) return;
    const {messages: _messages, gauges: _gauges, ...current} = transport;
    try {
      await updateTransport({
        ...current,
        ...(change.ticketNumber !== undefined ? {ticketNumber: change.ticketNumber} : {}),
        ...(change.quantity !== undefined ? {quantityDecimal: change.quantity} : {}),
      }).unwrap();
    } catch (failure) {
      // Rejecting keeps the field open with the reason beside it.
      throw new Error(extractApiErrorMessage(failure) ?? "The change could not be saved.");
    }
  }, [updateTransport]);

  const onViewChange = useCallback((view: DriverActivityViewState) => {
    const params: Record<string, string> = {};
    if (view.query) params.q = view.query;
    if (view.view !== "all") params.view = view.view;
    if (view.activeKey) params.open = view.activeKey;
    if (view.facets && activeFacetCount(view.facets)) params.filters = JSON.stringify(view.facets);
    setSearchParams(params, {replace: true});
  }, [setSearchParams]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    for (const neighbour of [subDays(date, 1), addDays(date, 1)]) {
      const arg = argFor(neighbour);
      if (arg) prefetch(arg);
    }
  }, [argFor, dispatch, prefetch]);

  const {hasAccess, user} = useAuth();
  // Costs stay hidden here for now: no revenue/cost column, totals, inspector charges, ticket cost or shift costing.
  const can = useCallback((permission: string) => (COST_VIEWS_ENABLED || permission !== "view_cost") && hasAccess(permission), [hasAccess]);
  // As in WaterGo, restricted users only look; costs are for those who may see them (the server masks them too).
  const restricted = user?.type?.code === personType.restricted.code;
  const showRevenue = can("view_cost");
  const isAdmin = user?.type?.code === personType.admin.code;
  const {navigate: completeTicket} = useTicketFormPageNavigate();

  // Download failures show as toasts from the app's OtsToastProvider.
  const ticketTools = useDriverActivityTicketTools({can});
  const ticketEdits = useDriverActivityTicketEditTools({can});
  // Merging shifts and visits checks what lies between the picked ones, so it is given the day.
  const structureTools = useDriverActivityStructureTools({rows: data, regionId, can});
  const quantityTools = useDriverActivityQuantityTools({timeZone: timeZone.value, can});
  // Admins save a shift's costs; everyone else previews them, as in WaterGo.
  const shiftCostTools = useDriverActivityShiftCostTools({regionId, rows: data, rateModelNames: display.rateModels, isAdmin, can});
  const bulkEdit = useDriverActivityBulkEditTools({regionId, can});
  // Saved shifts join the day in the dialog's own save; tickets made in DispatchGo's forms join it when saved.
  const createTools = useDriverActivityCreateTools({
    regionId,
    can,
    onCreateTicket: (draft) => {
      const {path, state} = ticketFormNavigation(draft);
      navigate(path, {state});
    },
    onCompleteTicket: (entry) => {
      const transport = entry.pickup ?? entry.delivery;
      if (transport) completeTicket(TransportEntity.from(transport));
    },
  });
  const openTicket = (path: string) => ([entry]: ActivityEntry[]) => {
    const tta = entry?.tta;
    if (tta) navigate(`${path}?id=${tta.id}&database=${tta.database}`);
  };
  const editTicket = ([entry]: ActivityEntry[]) => {
    if (entry?.tta) navigate("/tickets/form", {state: {id: entry.tta.id, database: entry.tta.database}});
    else if (entry?.pickup ?? entry?.delivery) navigate("/transports/form", {state: {id: (entry.pickup ?? entry.delivery)!.id}});
  };
  const pageActions = PAGE_ACTIONS.filter(({id}) => !PAGE_ACTION_PERMISSIONS[id] || can(PAGE_ACTION_PERMISSIONS[id]));
  const actionProvider = combineActionProviders(
    createActionProvider(pageActions, {
      view: openTicket("/tickets/profile"),
      map: openTicket("/tickets/profile/map"),
      edit: editTicket,
      "view-dispatch": ([entry]) => entry?.tta?.dispatchId && navigate(`/dispatch/profile?id=${entry.tta.dispatchId}`),
      "new-dispatch": ([entry]) => entry?.tta && navigate("/dispatch/form", {
        state: {formDefaults: toFormDefaultsFromTta(TransportAssocEntity.from(entry.tta))},
      }),
    }),
    ticketEdits,
    bulkEdit,
    structureTools,
    quantityTools,
    shiftCostTools,
    createTools,
    ticketTools,
  );

  // Drivers see their own day in FieldGo; the whole region's day is not theirs to see.
  if (user?.type?.code === personType.driver.code) return <Navigate to="/" replace/>;

  return (
    <ResizeableContent>
      {(size) => (
        <div style={{height: size.height, width: size.width}}>
          <DriverActivityWorkspace
            rows={data}
            audits={audits}
            date={new Date(appDttm)}
            onDateChange={onDateChange}
            timeZone={timeZone.value}
            // A day change keeps the previous day's rows on screen until the new ones arrive.
            isLoading={isLoading}
            isRefreshing={isFetching}
            error={error ? extractApiErrorMessage(error) ?? "Something went wrong." : undefined}
            onRetry={refetch}
            onRefresh={refetch}
            actionProvider={actionProvider}
            readOnly={restricted}
            showRevenue={showRevenue}
            facetKeys={restricted ? RESTRICTED_FACETS : undefined}
            onDownloadAttachments={onDownloadAttachments}
            onEditTransport={onEditTransport}
            renderAuditDetails={(entry) => entry.tta && (
              <ConnectedTicketAuditPanel ttaId={entry.tta.id} tenantCode={entry.tta.database} warnings={entry.warnings} allowRun/>
            )}
            renderMap={(entry) => <ConnectedActivityRouteMap entry={entry} apiKey={googleMapsApiKey}/>}
            renderDriverMap={(driver, openEntry) => (
              <ConnectedActivityDayMap driver={driver} apiKey={googleMapsApiKey} timeZone={timeZone.value} onOpenEntry={openEntry}/>
            )}
            initialView={initialView}
            onViewChange={onViewChange}
            initialDisplay={initialDisplay}
            onDisplayChange={onDisplayChange}
            rateModelOptions={shiftCostTools.rateModelOptions}
            lastUpdated={fulfilledTimeStamp}
            exportFileName={`driver-activity-${new Date(appDttm).toISOString().slice(0, 10)}.csv`}
            // Scroll position, ticked tickets, and folded drivers come back after a trip to a ticket or shift form.
            sessionKey={regionId ? `${regionId}:${startOfDay(appDttm).getTime()}` : undefined}/>
        </div>
      )}
    </ResizeableContent>
  );
};
